<script lang="ts" setup>
import { computed } from 'vue';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppBasePlaceholder } from '@libs/ui';

import type {
  ItinerarySegmentType,
  UpsellProductCardData,
} from '../../../models';
import { AppPlaceholderEventType } from '../../../models';
import { useRouterStore } from '../../../../../core/stores/routerStore';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ItineraryIcon from '../ItineraryIcon.vue';

import SuggestedActivitiesBanner from './SuggestedActivitiesBanner.vue';

const { t } = i18n.global;

interface ItineraryUpsellSegmentProps {
  data: UpsellProductCardData;
  segmentType: ItinerarySegmentType;
  isLoading?: boolean;
}

const props = defineProps<ItineraryUpsellSegmentProps>();

const routerStore = useRouterStore();
const recipeStore = useRecipeStore();
const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

const ingredientIndex = computed(() =>
  recipeStore.getIngredientIndex(props.data.ingredientId)
);

async function showActivities(): Promise<void> {
  await routerStore.navigateToActivitySelection({
    ingredientIndex: ingredientIndex.value,
  });
  statsigEventsStore.logTripOverviewExploreActivitiesClicked();
}

async function openActivity(
  activityCode: string,
  ingredientIndex: number
): Promise<void> {
  statsigEventsStore.logTripOverviewActivityBannerElementClicked();
  await routerStore.navigateToActivityDetailsInInTripOverview(
    { ingredientIndex },
    activityCode
  );
}
</script>

<template>
  <ItineraryTimeline
    v-if="
      props.data.genericActivityBanner.isVisible ||
      props.data.suggestedActivitiesBanner.isVisible
    "
    :segment-type="props.segmentType"
  >
    <template #icon>
      <ItineraryIcon
        v-if="props.data.suggestedActivitiesBanner.isVisible"
        :segment-type="props.segmentType"
      />
    </template>
    <template v-if="props.data.suggestedActivitiesBanner.isVisible" #header>
      <div class="segment-header">
        {{
          $t('planner.activity_suggestions.things_to_do', [
            props.data.suggestedActivitiesBanner.destination,
          ])
        }}
      </div>
    </template>

    <template #content>
      <slot />
      <SuggestedActivitiesBanner
        v-if="props.data.suggestedActivitiesBanner.isVisible"
        :cards="props.data.suggestedActivitiesBanner.suggestedActivities"
        :destination="props.data.suggestedActivitiesBanner.destination"
        :ingredient-index="ingredientIndex"
        @open-activity="openActivity($event, ingredientIndex)"
      />

      <AppBasePlaceholder
        v-if="props.data.genericActivityBanner.isVisible && !props.isLoading"
        :title="
          t('overview_placeholder.activity_upsell.title', [
            props.data.staticSuggestedActivitiesBanner.destination,
          ])
        "
        :description="t('overview_placeholder.activity_upsell.description')"
        :is-small-device="browserStore.isSmallerThan900px"
        :icon-properties="{
          name: 'activityOutlined',
          color: 'color-brand-coral',
        }"
        :actions="[
          {
            textToDisplay: t('placeholder.add_another_activity'),
            eventType: AppPlaceholderEventType.AddActivity,
          },
        ]"
        @action-clicked="showActivities"
      />
    </template>
  </ItineraryTimeline>

  <ItineraryTimeline
    v-if="props.data.staticSuggestedActivitiesBanner.isVisible"
    :segment-type="props.segmentType"
  >
    <template #banner>
      <AppBasePlaceholder
        class="activities-placeholder"
        :title="
          t('overview_placeholder.activity.title', [
            props.data.staticSuggestedActivitiesBanner.destination,
          ])
        "
        :description="t('overview_placeholder.activity.description')"
        :is-small-device="browserStore.isSmallerThan900px"
        :icon-properties="{
          name: 'activityOutlined',
          color: 'color-brand-coral',
        }"
        :actions="[
          {
            textToDisplay: t('placeholder.add_activity'),
            eventType: AppPlaceholderEventType.AddActivity,
          },
        ]"
        @action-clicked="showActivities"
      />
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.segment-header {
  @include font-label-m-bold;
}

.see-activities {
  white-space: nowrap;
  text-decoration: underline;
}

.see-activities-link-wrapper {
  display: flex;
  flex-direction: row;
  white-space: nowrap;
  gap: var(--spacing-8);
  .see-activities-text {
    text-decoration: underline;
  }
}
.activities-placeholder {
  @include for-less-or-equal-than-1199-px {
    margin-left: var(--spacing-8);
  }
  @include for-less-or-equal-than-599-px {
    margin-left: 0;
  }
}
</style>
